import React from "react";
import { Link } from "react-router-dom";
import {
 ArrowUpRight, Sun, Battery, Zap, Home as HomeIcon, Building2, Factory,
 Sprout, Download, Check
} from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);

export default function EnergiesRenouvelables() {
 const tt = useT();

 const SEGMENTS = [
 { i: HomeIcon,
 t: tt("Solaire résidentiel", "Residential solar", "Solar residencial"),
 d: tt("Kits solaires domestiques, autoconsommation et solutions autonomes pour foyers et villas.",
 "Domestic solar kits, self-consumption and off-grid solutions for homes and villas.",
 "Kits solares domésticos, autoconsumo e soluções autónomas para lares e vivendas.") },
 { i: Building2,
 t: tt("Solaire tertiaire", "Commercial solar", "Solar terciário"),
 d: tt("Bureaux, commerces, hôtels, cliniques, écoles. Optimisation de la facture énergétique.",
 "Offices, retail, hotels, clinics, schools. Energy bill optimisation.",
 "Escritórios, comércio, hotéis, clínicas, escolas. Optimização da factura energética.") },
 { i: Factory,
 t: tt("Solaire industriel & agro", "Industrial & agricultural solar", "Solar industrial e agrícola"),
 d: tt("Toitures industrielles, process, chaîne du froid, séchage, transformation agricole.",
 "Industrial rooftops, process, cold chain, drying and agricultural processing.",
 "Coberturas industriais, processo, cadeia de frio, secagem, transformação agrícola.") },
 { i: Sprout,
 t: tt("Pompage solaire agricole", "Agricultural solar pumping", "Bombagem solar agrícola"),
 d: tt("Irrigation, forage, adduction d'eau : kits standardisés pour maraîchers et coopératives.",
 "Irrigation, boreholes, water supply: standardised kits for market gardeners and cooperatives.",
 "Irrigação, furos, adução de água: kits standardizados para horticultores e cooperativas.") },
 { i: Zap,
 t: tt("Éclairage public solaire", "Solar street lighting", "Iluminação pública solar"),
 d: tt("Lampadaires autonomes, éclairage de sécurité, éclairage de sites isolés.",
 "Autonomous street lights, security lighting, off-grid site lighting.",
 "Candeeiros autónomos, iluminação de segurança, iluminação de locais isolados.") },
 { i: Battery,
 t: tt("Stockage lithium & backup", "Lithium storage & backup", "Armazenamento lítio e backup"),
 d: tt("Batteries de nouvelle génération pour continuité de service, chaîne du froid, salles techniques.",
 "Next-generation batteries for service continuity, cold chain and technical rooms.",
 "Baterias de nova geração para continuidade de serviço, cadeia de frio, salas técnicas.") },
 ];

 const KPI = [
 { n: "2 800 h",
 l: tt("Ensoleillement annuel moyen", "Average annual sunshine", "Insolação anual média"),
 d: tt("Un des potentiels solaires les plus élevés d'Afrique de l'Ouest.",
 "One of the highest solar potentials in West Africa.",
 "Um dos potenciais solares mais elevados da África Ocidental.") },
 { n: "5,5 kWh/m²",
 l: tt("Irradiation quotidienne moyenne", "Average daily irradiation", "Irradiação diária média"),
 d: tt("Rendement photovoltaïque idéal toute l'année.",
 "Ideal photovoltaic yield all year round.",
 "Rendimento fotovoltaico ideal durante todo o ano.") },
 { n: "< 40 %",
 l: tt("Taux d'accès à l'électricité", "Electricity access rate", "Taxa de acesso à electricidade"),
 d: tt("Une marge de développement considérable hors capitale.",
 "A considerable development margin outside the capital.",
 "Uma margem de desenvolvimento considerável fora da capital.") },
 { n: "70 %+",
 l: tt("Population rurale hors réseau", "Off-grid rural population", "População rural fora da rede"),
 d: tt("Cible naturelle du solaire autonome et des mini-réseaux.",
 "A natural target for off-grid solar and mini-grids.",
 "Alvo natural do solar autónomo e das mini-redes.") },
 ];

 const METHODE = [
 { n: "01", t: tt("Audit énergétique", "Energy audit", "Auditoria energética"),
 d: tt("Analyse des usages, courbe de charge, contraintes de site.",
 "Usage analysis, load curve, site constraints.",
 "Análise dos usos, curva de carga, restrições do local.") },
 { n: "02", t: tt("Étude & dimensionnement", "Study & sizing", "Estudo e dimensionamento"),
 d: tt("Bilan solaire, choix des panneaux, onduleurs, batteries, structure.",
 "Solar balance, choice of panels, inverters, batteries, mounting.",
 "Balanço solar, escolha de painéis, inversores, baterias, estrutura.") },
 { n: "03", t: tt("Devis & financement", "Quote & financing", "Orçamento e financiamento"),
 d: tt("Chiffrage transparent et accompagnement des solutions de financement.",
 "Transparent pricing and support for financing solutions.",
 "Orçamento transparente e acompanhamento das soluções de financiamento.") },
 { n: "04", t: tt("Installation", "Installation", "Instalação"),
 d: tt("Pose des structures, câblage, mise en service, contrôles de sécurité.",
 "Structure fitting, wiring, commissioning, safety checks.",
 "Colocação das estruturas, cablagem, entrada em serviço, controlos de segurança.") },
 { n: "05", t: tt("Suivi de production", "Production monitoring", "Acompanhamento da produção"),
 d: tt("Supervision, alertes, rapports mensuels.",
 "Supervision, alerts, monthly reports.",
 "Supervisão, alertas, relatórios mensais.") },
 { n: "06", t: tt("Maintenance longue durée", "Long-term maintenance", "Manutenção de longa duração"),
 d: tt("Contrats préventifs, nettoyage, remplacement de composants.",
 "Preventive contracts, cleaning, component replacement.",
 "Contratos preventivos, limpeza, substituição de componentes.") },
 ];

 const ATOUTS = [
 tt("Fabricants et fournisseurs internationalement reconnus", "Internationally recognised manufacturers and suppliers", "Fabricantes e fornecedores reconhecidos internacionalmente"),
 tt("Équipements adaptés au climat tropical et à la poussière", "Equipment suited to tropical climate and dust", "Equipamentos adaptados ao clima tropical e à poeira"),
 tt("Techniciens formés localement, réactivité sur site", "Locally trained technicians, on-site responsiveness", "Técnicos formados localmente, reactividade em obra"),
 tt("Chaîne d'approvisionnement fiable via Bissau", "Reliable supply chain via Bissau and Bissau", "Cadeia de abastecimento fiável via Bissau e Bissau"),
 tt("Solutions modulaires, évolutives dans le temps", "Modular solutions, evolving over time", "Soluções modulares, evolutivas no tempo"),
 tt("Documentation technique complète en français, portugais et anglais", "Full technical documentation in French, Portuguese and English", "Documentação técnica completa em francês, português e inglês"),
 ];

 return (
 <div data-testid="page-energies-renouvelables">
 <Seo />

 {/* HERO */}
 <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-nanda-ink">
 <img
 src="/photos/sector-energie-hero.jpg"
 alt={tt("Installation solaire photovoltaïque en Afrique de l'Ouest par NANDA SÁ ENERGIA PRO",
 "Photovoltaic solar installation in West Africa by NANDA SÁ ENERGIA PRO",
 "Instalação solar fotovoltaica na África Ocidental pela NANDA SÁ ENERGIA PRO")}
 className="absolute inset-0 h-full w-full object-cover opacity-70"
 loading="eager"
 />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/50 via-nanda-ink/25 to-nanda-ink/80" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">{tt("NANDA SÁ ENERGIA PRO · Solaire & stockage", "NANDA SÁ ENERGIA PRO · Solar & storage", "NANDA SÁ ENERGIA PRO · Solar e armazenamento")}</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
 {tt("Énergies renouvelables.", "Renewable energy.", "Energias renováveis.")}<br />
 <span className="text-nanda-gold italic">{tt("Solaire, stockage & efficacité.", "Solar, storage & efficiency.", "Solar, armazenamento e eficiência.")}</span>
 </h1>
 <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed">
 {tt(
 "Étude, fourniture et installation de systèmes solaires photovoltaïques, de stockage lithium et de solutions d'efficacité énergétique pour les particuliers, les entreprises et les institutions.",
 "Design, supply and installation of photovoltaic solar systems, lithium storage and energy efficiency solutions for individuals, businesses and institutions.",
 "Estudo, fornecimento e instalação de sistemas solares fotovoltaicos, armazenamento de lítio e soluções de eficiência energética para particulares, empresas e instituições."
 )}
 </p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link to="/devis?category=energies-renouvelables" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-gold hover:text-white transition-colors duration-500" data-testid="er-cta-devis">
 {tt("Demander un devis solaire", "Request a solar quote", "Solicitar orçamento solar")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a href="/pdf/nanda-sa-note-energie.pdf" download className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="er-cta-pdf">
 {tt("Télécharger la note énergie", "Download the energy brief", "Descarregar a nota energia")} <Download size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </div>
 </section>

 {/* CONTEXTE */}
 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Le contexte", "The context", "O contexto")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Le soleil comme ressource première.", "The sun as a primary resource.", "O sol como recurso primeiro.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt(
 "La Guinée-Bissau bénéficient de l'un des potentiels solaires les plus élevés d'Afrique de l'Ouest. Le solaire n'est plus un pari : c'est déjà, économiquement, la solution la plus compétitive face au diesel et au groupe électrogène.",
 "Guinea-Bissau benefit from one of the highest solar potentials in West Africa. Solar is no longer a bet: it is already, economically, the most competitive solution against diesel and generators.",
 "A Guiné-Bissau beneficiam de um dos potenciais solares mais elevados da África Ocidental. O solar já não é uma aposta: é já, economicamente, a solução mais competitiva face ao gasóleo e ao gerador."
 )}</p>
 <p>{tt(
 "NANDA SÁ ENERGIA PRO déploie des solutions concrètes pour les foyers, les commerces, les industries et les collectivités : équipements sélectionnés auprès de fabricants reconnus, installation soignée, maintenance locale.",
 "NANDA SÁ ENERGIA PRO deploys concrete solutions for households, shops, industries and communities: equipment sourced from recognised manufacturers, careful installation and local maintenance.",
 "A NANDA SÁ ENERGIA PRO implementa soluções concretas para lares, comércio, indústrias e autarquias: equipamentos seleccionados junto de fabricantes reconhecidos, instalação cuidada, manutenção local."
 )}</p>
 <p>{tt(
 "Notre approche est simple : on écoute le besoin, on dimensionne juste, on installe proprement, et on reste disponible dans la durée.",
 "Our approach is simple: we listen to the need, we size accurately, we install cleanly, and we stay available over time.",
 "A nossa abordagem é simples: escutamos a necessidade, dimensionamos com rigor, instalamos com esmero e mantemo-nos disponíveis no longo prazo."
 )}</p>
 </FadeUp>
 </div>
 </Section>

 {/* KPI */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Chiffres clés", "Key figures", "Números-chave")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Un potentiel solaire de premier rang.", "First-class solar potential.", "Um potencial solar de primeira ordem.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {KPI.map((k) => (
 <StaggerItem key={k.l} className="bg-white p-8 md:p-10">
 <div className="font-serif text-nanda-terracotta text-4xl md:text-5xl mb-4 tabular-nums">{k.n}</div>
 <div className="font-serif text-xl text-nanda-ink leading-tight">{k.l}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{k.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 <p className="mt-6 text-xs text-nanda-muted italic">
 {tt("Sources publiques : IRENA, Banque mondiale (ESMAP), AFD, BAD.",
 "Public sources: IRENA, World Bank (ESMAP), AFD, AfDB.",
 "Fontes públicas: IRENA, Banco Mundial (ESMAP), AFD, BAD.")}
 </p>
 </Section>

 {/* SEGMENTS */}
 <Section id="segments">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos segments d'intervention", "Our segments of intervention", "Os nossos segmentos de intervenção")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six segments, une même exigence.", "Six segments, one same standard.", "Seis segmentos, uma mesma exigência.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {SEGMENTS.map((s) => (
 <StaggerItem key={s.t} className="bg-white p-8 md:p-10">
 <s.i size={28} strokeWidth={1.2} className="text-nanda-terracotta mb-5" />
 <div className="font-serif text-2xl text-nanda-ink leading-tight">{s.t}</div>
 <p className="mt-4 text-[15px] text-nanda-inkSoft leading-relaxed">{s.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* MÉTHODE */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Notre méthode", "Our method", "O nosso método")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("De l'audit à la maintenance.", "From audit to maintenance.", "Da auditoria à manutenção.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {METHODE.map((m) => (
 <StaggerItem key={m.n} className="bg-white p-8 md:p-10">
 <div className="font-serif text-nanda-terracotta text-4xl md:text-5xl mb-4">{m.n}</div>
 <div className="font-serif text-xl md:text-2xl text-nanda-ink leading-tight">{m.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{m.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* ATOUTS */}
 <Section>
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Nos atouts", "Our strengths", "As nossas vantagens")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six raisons de nous choisir.", "Six reasons to choose us.", "Seis razões para nos escolher.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
 {ATOUTS.map((a) => (
 <div key={a} className="flex items-start gap-3 py-4 border-t border-nanda-line">
 <Check size={18} strokeWidth={2} className="text-nanda-terracotta mt-1 shrink-0" />
 <span className="text-sm md:text-[15px] text-nanda-ink leading-snug">{a}</span>
 </div>
 ))}
 </div>
 </Section>

 {/* CTA */}
 <Section className="bg-nanda-ink text-white">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-nanda-gold">{tt("Parlons de votre projet", "Let's talk about your project", "Falemos do seu projecto")}</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">
 {tt("Un devis solaire,", "A solar quote,", "Um orçamento solar,")}<br />
 <span className="text-nanda-gold italic">{tt("un audit énergétique, un projet d'ampleur ?", "an energy audit, a large-scale project?", "uma auditoria energética, um projecto de grande dimensão?")}</span>
 </h3>
 <div className="mt-12 flex flex-wrap gap-4 justify-center">
 <Link to="/devis?category=energies-renouvelables" className="inline-flex items-center gap-3 bg-nanda-gold text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="er-cta-final">
 {tt("Demander un devis", "Request a quote", "Solicitar orçamento")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a href="/pdf/nanda-sa-note-energie.pdf" download className="inline-flex items-center gap-3 border border-white/50 text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="er-cta-note">
 {tt("Télécharger la note énergie", "Download the energy brief", "Descarregar a nota energia")} <Download size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
